/* API layer — REST calls to the backend */

import { state } from './state.js';
import { authHeader, refreshAccessToken, getAuthMode, getActiveConnection } from './auth.js';

// Central fetch: attaches the bearer access token (hosted mode) and, on a 401, does a single silent
// refresh + one retry. In local/desktop mode there is no access token, so this is a no-op and
// behavior is unchanged.
async function apiFetch(url, opts = {}, _retried = false) {
  const merged = { ...opts, headers: { ...(opts && opts.headers), ...authHeader() } };
  const res = await fetch(url, merged);
  if (res.status === 401 && !_retried && getAuthMode() === "hosted") {
    if (await refreshAccessToken()) return apiFetch(url, opts, true);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    // Hosted routes surface gate/validation reasons as {detail: ...} (FastAPI/JSONResponse);
    // local tool routes use {error: ...}. Prefer whichever is present so 4xx reasons (e.g. the
    // promotion ladder/lineage gates) reach the UI instead of a bare "HTTP 400".
    const err = new Error(body.error || body.detail || `HTTP ${res.status}`);
    // Additive: the HTTP status code, so callers that need to structurally discriminate a
    // real server response (definite failure) from a network-level rejection (ambiguous --
    // see promotion-wizard.js's classifyApplyError) don't have to prose-match the message.
    // Every existing catch site keeps working unchanged (they only ever read `.message`).
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Same auth + 401-refresh-retry contract as apiFetch, but hands back the RAW Response
// for callers that read the body themselves (uploads, file/CSV downloads, workspace
// reads). These used to call fetch() directly, so an expired token surfaced as a bare
// 401 with no refresh -- one of the ways a tab ended up needing a full reload.
async function rawFetch(url, opts = {}, _retried = false) {
  const merged = { ...opts, headers: { ...(opts && opts.headers), ...authHeader() } };
  const res = await fetch(url, merged);
  if (res.status === 401 && !_retried && getAuthMode() === "hosted") {
    if (await refreshAccessToken()) return rawFetch(url, opts, true);
  }
  return res;
}

// Per-tenant tool route — gates on `needs_session` so local-only tools
// (rewind journal readers) run offline.
function _tenantTool(toolName) {
  return `/api/tenants/${encodeURIComponent(state.tenant || "default")}/tools/${toolName}`;
}

// HTTP /api/tools/{name} returns {tool, success, result|error}. Unwrap to the
// typed `result` on success; surface a tool-level failure as a thrown Error.
function _unwrapTool(body) {
  if (body && body.success === false) {
    const e = new Error(body.error || "tool failed");
    e.toolError = body;
    throw e;
  }
  return body && Object.prototype.hasOwnProperty.call(body, "result") ? body.result : body;
}

function encodePath(p) { return p.split("/").map(encodeURIComponent).join("/"); }

// Connection meta cache — used by undoPreview / undoExecute to surface env_role / label
// for the prod typed-confirm gate.  Keyed by connection ID so a second call in the same
// browser session is free.  Null in local/desktop mode (getActiveConnection() is null).
let _connMetaCache = null;
let _connMetaCacheId = null;

async function _fetchConnMeta() {
  const cid = getActiveConnection();
  if (!cid || getAuthMode() !== "hosted") return null; // local/desktop or non-hosted — no fetch needed
  if (_connMetaCacheId === cid && _connMetaCache) return _connMetaCache;
  const data = await apiFetch("/api/connections");
  const conns = (data && data.connections) || [];
  _connMetaCache = conns.find((c) => c.id === cid) || null;
  _connMetaCacheId = cid;
  return _connMetaCache;
}

// DB inspector base: per-connection in hosted, single-operator /api/db in local.
function _dbBase() {
  const cid = getActiveConnection();
  return (getAuthMode() === "hosted" && cid) ? `/api/connections/${encodeURIComponent(cid)}/db` : "/api/db";
}

export const api = {
  get: (url) => apiFetch(url),
  post: (url, body) => apiFetch(url, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  }),
  health: () => apiFetch("/api/health"),
  agentStatus: () => apiFetch("/api/agent/status"),
  // POST /api/agent/autonomy -- toggles the active tenant's autonomy mode.
  // Returns the freshly-resolved status payload (same shape as agentStatus()
  // above) so a caller can apply it directly (see agent-store.js's
  // applyAutonomyStatus()) without a follow-up GET.
  setAutonomy: (autonomy) => apiFetch("/api/agent/autonomy", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ autonomy }),
  }),
  hostedModels: () => apiFetch("/api/models"),
  agentToolCatalog: () => apiFetch("/api/agent/tool-catalog"),
  fileTree: (tenant) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) return apiFetch(`/api/connections/${encodeURIComponent(cid)}/files`);
    return apiFetch(`/api/files?tenant=${encodeURIComponent(tenant || state.tenant)}`);
  },
  // `source` ("cloud" | "workspace") selects which server-side copy answers the request:
  // "workspace" serves the R2 snapshot copy instead of a live SAP fetch. Hosted-only --
  // desktop has no snapshot/workspace concept, so the param is silently dropped there.
  fileContent: (path, source) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      const src = source && source !== "cloud" ? `&source=${encodeURIComponent(source)}` : "";
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/files/content?path=${encodeURIComponent(path)}${src}`);
    }
    return apiFetch(`/api/files/${encodePath(path)}?tenant=${encodeURIComponent(state.tenant)}`);
  },
  writeFile: (path, content) => apiFetch(`/api/files/${encodePath(path)}?tenant=${encodeURIComponent(state.tenant)}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content }),
  }),
  deleteFile: (path) => apiFetch(`/api/files/${encodePath(path)}?tenant=${encodeURIComponent(state.tenant)}`, {
    method: "DELETE",
  }),
  gitStatus: () => apiFetch("/api/sync/git-status"),
  // Read-only git commit history for the tenant sync dir (Sync view's History tab).
  syncHistory: (limit) => apiFetch(`/api/sync/history${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`),
  syncStatus: (kind, refresh) => {
    const params = new URLSearchParams();
    if (kind) params.set("kind", kind);
    if (refresh) params.set("refresh", "true");
    const qs = params.toString();
    return apiFetch(`/api/sync/status${qs ? `?${qs}` : ""}`);
  },
  syncDiff: (kind, name) => apiFetch(`/api/sync/diff/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`),
  syncPush: (kind, name) => apiFetch(`/api/sync/push/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`, { method: "POST" }),
  syncPull: (kind, name) => apiFetch(`/api/sync/pull/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`, { method: "POST" }),
  openInEditor: (path, editor) => apiFetch("/api/open-in-editor", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path, editor: editor || undefined }),
  }),
  listEditors: () => apiFetch("/api/editors"),
  listTenants: () => apiFetch("/api/tenants"),
  // authMode "password" (default) | "client_credentials" -- only that mode's credential
  // pair is sent, mirroring the hosted add-connection form's body shape.
  tenantLogin: ({ url, username = "", password = "", authMode = "password", clientId, clientSecret, remember }) =>
    apiFetch("/api/tenants/login", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        url, remember,
        auth_mode: authMode,
        ...(authMode === "client_credentials"
          ? { client_id: clientId, client_secret: clientSecret }
          : { username, password }),
      }),
    }),
  tenantSwitch: (name) => apiFetch("/api/tenants/switch", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  }),
  tenantRemove: (name) => apiFetch(`/api/tenants/${encodeURIComponent(name)}`, { method: "DELETE" }),
  getPipelines: () => apiFetch("/api/promotion/pipelines"),
  setLineage: (name, family, env) => apiFetch(`/api/tenants/${encodeURIComponent(name)}/lineage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ family: family || null, env: env || null }),
  }),
  promotionPlan: (source, target, includeOptin) => apiFetch("/api/promotion/plan", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source, target,
      ...(Array.isArray(includeOptin) && includeOptin.length ? { include_optin: includeOptin } : {}) }),
  }),
  promotionApply: (source, target, opts = {}) => apiFetch("/api/promotion/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source, target, ...opts }),
  }),
  promotionReverse: (target, run_id, opts = {}) => apiFetch("/api/promotion/reverse", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, run_id, ...opts }),
  }),
  promotionDeleteItem: (target, family, asset_id, opts = {}) => apiFetch("/api/promotion/delete-item", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, family, asset_id, ...opts }),
  }),
  // GET promotion RUN history (the applies). Local: all families; hosted: the given (default: active)
  // connection's family. Returns {runs:[{run_id, source, target, target_env, status,
  // results:[...], recorded_at, family}]} newest-first. Empty on error (graceful).
  promotionRuns: (cid) => {
    cid = cid || getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/promotion/runs`).catch(() => ({ runs: [] }));
    return apiFetch("/api/promotion/runs").catch(() => ({ runs: [] }));
  },
  // POST reversibility verdict for one run (target-scoped rewind-journal rollup; read-only).
  // Returns {verdict:"reversible"|"risky"|"not_reversible"|"unknown", counts, blocking:[...], total}.
  // Hosted journal-preview is deferred: hosted returns a neutral "unknown" so the card degrades
  // to "computed at revert" rather than showing a wrong verdict.
  promotionReversePreview: (target, run_id) => {
    if (getAuthMode() === "hosted" && getActiveConnection())
      return Promise.resolve({ verdict: "unknown", counts: {}, blocking: [], total: 0 });
    return apiFetch("/api/promotion/reverse/preview", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target, run_id }),
    }).catch(() => ({ verdict: "unknown", counts: {}, blocking: [], total: 0 }));
  },
  // Read-only system-template catalog (F1) -- backs the Templates view's SYSTEM group.
  // Response: {templates: [{name, page?, is_system: true}, ...]}. Local-only for now (no
  // hosted mirror yet); a fetch failure is the caller's signal to render Custom-only.
  templatesCatalog: () => apiFetch("/api/templates/catalog"),
  templateSlots: (page) => {
    const params = page ? `?page=${encodeURIComponent(page)}` : "";
    return apiFetch(`/api/sync/template-slots${params}`);
  },
  scaffoldTemplate: (page, slot, name) => apiFetch("/api/sync/scaffold", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ page, slot, name }),
  }),
  syncStash: (kind, name) => apiFetch(`/api/sync/stash/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`, { method: "POST" }),
  syncStashAll: () => apiFetch("/api/sync/stash-all", { method: "POST" }),
  configuratorTraces: (filter) =>
    apiFetch(`/api/configurator/traces?filter=${encodeURIComponent(filter || "script")}`),

  // DB Inspector (per-connection in hosted via _dbBase)
  dbTables: () => apiFetch(`${_dbBase()}/tables`),
  dbSchema: (tableName) => apiFetch(`${_dbBase()}/tables/${encodeURIComponent(tableName)}/schema`),
  dbData: (tableName, params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    if (params.orderBy) qs.set("orderBy", params.orderBy);
    if (params.dir) qs.set("dir", params.dir);
    if (params.where) qs.set("where", params.where);
    const q = qs.toString();
    return apiFetch(`${_dbBase()}/tables/${encodeURIComponent(tableName)}/data${q ? `?${q}` : ""}`);
  },
  dbStats: (tableName) => apiFetch(`${_dbBase()}/tables/${encodeURIComponent(tableName)}/stats`),
  // Batched read-only row/column counts (F2) -- ONE call per viewport batch instead of
  // one per rendered row. `names` is a plain array; capped server-side (~25/request) --
  // callers should already be chunking to roughly that size. Response: {stats: {name:
  // {rows, columns, is_custom} | null}, truncated?, cap?}. _dbBase() so this is already
  // hosted-ready (per-connection) the day a hosted mirror route exists -- see dbTables().
  dbTableStats: (names) => apiFetch(`${_dbBase()}/tables/stats?names=${encodeURIComponent(names.join(","))}`),
  // CSV export: GET {_dbBase()}/tables/{t}/export.csv?orderBy=&dir=&where= -> text/csv
  // attachment, with an X-Truncated: true header when the 100,000-row cap is hit.
  // Pure URL builder -- unit-tested directly (api-db-export-csv.test.mjs).
  dbExportCsvUrl: (table, { orderBy, dir, where } = {}) => {
    const p = new URLSearchParams();
    if (orderBy) p.set("orderBy", orderBy);
    if (dir) p.set("dir", dir);
    if (where) p.set("where", where);
    const q = p.toString();
    return `${_dbBase()}/tables/${encodeURIComponent(table)}/export.csv${q ? `?${q}` : ""}`;
  },
  // Download a table's CSV export -- hosted: auth-gated fetch -> Blob -> temp anchor (mirrors
  // downloadAuditCsv below; apiFetch can't return a Blob); local: direct URL anchor
  // (unauthenticated /api/db/... endpoint). Returns whether the response carried
  // X-Truncated: true (hosted only -- a direct anchor can't read response headers, so local
  // always resolves false) so the caller can toast a "truncated at 100,000 rows" notice.
  downloadDbCsv: async (table, opts = {}) => {
    const url = api.dbExportCsvUrl(table, opts);
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      const res = await rawFetch(url, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error(`CSV export failed (HTTP ${res.status})`);
      const truncated = res.headers.get("X-Truncated") === "true";
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${table}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return truncated;
    }
    // Local mode: direct URL anchor (unauthenticated endpoint) -- can't read response headers.
    const a = document.createElement("a");
    a.href = url;
    a.download = `${table}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return false;
  },
  // Find-in-files — hosted: the per-connection live-SAP search (same result shape as the
  // desktop index search, so both search surfaces render unchanged); local: the desktop
  // tenant-dir search. Mirrors the fileContent/fieldsTree gate.
  searchFiles: (query, opts = {}) => {
    const params = new URLSearchParams({ q: query });
    if (opts.regex) params.set("regex", "true");
    if (opts.caseSensitive) params.set("case_sensitive", "true");
    if (opts.kind && opts.kind !== "all") params.set("kind", opts.kind);
    if (opts.max) params.set("max", String(opts.max));
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/files/search?${params.toString()}`);
    if (state.tenant) params.set("tenant", state.tenant);
    return apiFetch(`/api/files/search?${params.toString()}`);
  },

  // Fields tree
  fieldsTree: () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) return apiFetch(`/api/connections/${encodeURIComponent(cid)}/fields-tree`);
    return apiFetch("/api/fields-tree");
  },
  // Execution-order drill-down — per-connection in hosted (the desktop routes use the
  // ambient tenant session, gated there), else the desktop routes. Mirrors fieldsTree.
  fieldCalculations: (fieldId) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/fields-tree/${fieldId}/calculations`);
    return apiFetch(`/api/fields-tree/${fieldId}/calculations`);
  },
  calculationUsage: (calcName) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/calculations/${encodeURIComponent(calcName)}/usage`);
    return apiFetch(`/api/calculations/${encodeURIComponent(calcName)}/usage`);
  },
  // Global-script metadata + execution order: per-connection in hosted (the desktop routes
  // read control-plane-local disk / the ambient tenant, so they 404 in hosted), else the
  // single-operator desktop routes. Mirrors fieldsTree above.
  scriptsMetadata: () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) return apiFetch(`/api/connections/${encodeURIComponent(cid)}/scripts/metadata`);
    return apiFetch(`/api/scripts/metadata?tenant=${encodeURIComponent(state.tenant || "")}`);
  },
  executionOrder: (scope = "quote") => {
    const cid = getActiveConnection();
    const q = `scope=${encodeURIComponent(scope)}`;
    if (getAuthMode() === "hosted" && cid) return apiFetch(`/api/connections/${encodeURIComponent(cid)}/execution-order?${q}`);
    return apiFetch(`/api/execution-order?${q}`);
  },
  // Hosted-only quote-table script list -- the Code view's desktop path reads
  // state.syncStatusMap (empty in hosted). Returns [{table, scripts:[names]}] in hosted,
  // else null (the caller keeps using syncStatusMap). Mirrors the scriptsMetadata gate.
  quoteTables: () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/quote-tables`);
    return Promise.resolve(null);
  },
  // Hosted-only workspace-changes surface: which files the agent changed in this
  // connection's sandbox workspace (from the R2 snapshot + the pull ledger). Local mode
  // resolves null -- the desktop app has the Sync view for this.
  workspaceChanges: () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/workspace-changes`);
    return Promise.resolve(null);
  },
  // Native version citation for one changed file (global scripts + custom calcs only;
  // ledger-only types answer {}). Always resolves an object -- failure is {}.
  workspaceCitation: (path) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/workspace-changes/citation?path=${encodeURIComponent(path)}`);
    return Promise.resolve({});
  },
  // Guarded per-file workspace push/discard. These hand back a RAW {status, body} instead
  // of apiFetch's throw-on-error, because for these two routes a non-200 is not merely a
  // failure to report: a 409 carrying `diffs` IS the conflict-resolution payload the
  // dialog renders, a 402 means "plan", and a 502's `detail` is SAP's own words. Callers
  // must branch on `status`, never on a thrown message. rawFetch already attaches the
  // bearer + does the single silent 401 refresh/retry.
  //
  // `force: true` is only ever sent from the conflict dialog's explicit "Overwrite cloud
  // anyway" exit -- the key is ABSENT (not false) on a normal push, so a request body can
  // never be mistaken for an override that was never chosen.
  workspacePush: async (path, force) => {
    const cid = getActiveConnection();
    if (getAuthMode() !== "hosted" || !cid)
      return { status: 0, body: { detail: "workspace push needs a hosted connection" } };
    const res = await rawFetch(`/api/connections/${encodeURIComponent(cid)}/workspace-changes/push`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(force ? { path, force: true } : { path }),
    });
    return { status: res.status, body: await res.json().catch(() => ({})) };
  },
  workspaceDiscard: async (path) => {
    const cid = getActiveConnection();
    if (getAuthMode() !== "hosted" || !cid)
      return { status: 0, body: { detail: "workspace discard needs a hosted connection" } };
    const res = await rawFetch(`/api/connections/${encodeURIComponent(cid)}/workspace-changes/discard`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    });
    return { status: res.status, body: await res.json().catch(() => ({})) };
  },
  // Custom-template metadata is a desktop-only sidecar read ({tenant}/ui-templates/
  // properties/*.json on the control plane's own disk): there is no per-connection
  // hosted counterpart, and hosted never has an ambient tenant, so the call could only
  // ever fire as `?tenant=` and 404 — once per file-tree load, including every
  // connection switch and new chat. Resolve empty instead; the tree's template rows
  // simply carry no extra metadata where none exists.
  templatesMetadata: () => {
    if (getAuthMode() === "hosted" || !state.tenant) return Promise.resolve({});
    return apiFetch(`/api/templates/metadata?tenant=${encodeURIComponent(state.tenant)}`);
  },
  // Hosted-only combined templates fetch (system + custom) -- the desktop path uses
  // templatesCatalog() (system) + syncStatusMap (custom), neither of which exists in hosted.
  // Returns {system:[{name,page?}], custom:[{name,page?}]} in hosted, else null (the caller
  // then runs its existing two-source path). Mirrors the fieldsTree/scriptsMetadata gate.
  templatesAll: () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/templates`);
    return Promise.resolve(null);
  },
  fieldReorder: (fieldId, calculationIds) => apiFetch(`/api/fields-tree/${fieldId}/reorder`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ calculationIds }),
  }),

  // Per-connection conversations (hosted: the server-issued, ownership-gated conversation surface)
  connectionConversations: (cid) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations`),
  createConnectionConversation: (cid, title) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: title || null }),
    }),
  renameConnectionConversation: (cid, coid, title) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(coid)}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }),
    }),
  deleteConnectionConversation: (cid, coid) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(coid)}`,
      { method: "DELETE" }),
  connectionMessages: (cid, coid) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(coid)}/messages`),
  // Whole-document transcript write. { replace: true } = an explicit history rewrite
  // (regenerate / edit-and-resend truncation); a bare PUT is merged server-side so a
  // stale copy of the log can never erase turns written by another tab.
  putConnectionMessages: (cid, coid, messages, opts = {}) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(coid)}/messages`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(opts.replace ? { messages, replace: true } : { messages }),
    }),
  // Per-turn transcript append, idempotent by the turn's message id — the routine
  // persist (question on send, question+answer on final). Never clobbers.
  appendConnectionTurn: (cid, coid, messages) =>
    apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(coid)}/messages/turn`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ messages }),
    }),

  // Conversation history
  // Hosted: the legacy single-operator list is gated, so re-point to the
  // per-connection conversations and adapt each row to the legacy list shape
  // the consumers render (title/date; the hosted rows carry no count/summary).
  // The hosted route returns oldest-first (created_at ascending) — reverse to
  // the newest-first order every consumer assumes.
  listConversations: async () => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      const d = await apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations`);
      const conversations = ((d && d.conversations) || []).map((v) => ({
        id: v.id, title: v.title || "", date: v.created_at || "",
        count: null, summary: "", preview: v.preview || "", favourite: !!v.favourite,
        // A run streaming right now (server truth from the durable run registry):
        // {run_id, started_at} | null. Drives the list's "Live" chip; opening the
        // conversation already resumes it (resume-on-open), so the chip needs no
        // click behavior of its own.
        active_run: v.active_run || null,
      })).reverse();
      return { conversations };
    }
    return apiFetch("/api/conversations");
  },
  saveConversation: (messages, title, summary) => apiFetch("/api/conversations", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, title: title || null, summary: summary || null }),
  }),
  activateConversation: (id) => apiFetch(`/api/conversations/${encodeURIComponent(id)}/activate`, { method: "POST" }),
  deleteConversation: (id) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    return apiFetch(`/api/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Rename a conversation's title. Hosted routes to the ownership-gated
  // per-connection PATCH; local mode PATCHes the file-backed store.
  renameConversation: (id, title) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(id)}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }),
      });
    }
    return apiFetch(`/api/conversations/${encodeURIComponent(id)}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }),
    });
  },
  // (Un)favourite a conversation. Hosted routes to the per-connection PATCH
  // (favourite field); local mode POSTs the file-store favourite endpoint.
  setConversationFavourite: (id, favourite) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/conversations/${encodeURIComponent(id)}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ favourite }),
      });
    }
    return apiFetch(`/api/conversations/${encodeURIComponent(id)}/favourite`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ favourite }),
    });
  },
  resetConversation: () => apiFetch("/api/conversations/reset", { method: "POST" }),

  // Beacon-based save (for beforeunload — fire-and-forget)
  saveConversationBeacon: (messages, title) => {
    const body = JSON.stringify({ messages, title: title || null });
    navigator.sendBeacon("/api/conversations", new Blob([body], { type: "application/json" }));
  },

  // Knowledge browsing
  knowledgeClasses: (search, kind) => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (kind) p.set("kind", kind);
    return apiFetch(`/api/knowledge/classes?${p}`);
  },
  knowledgeClass: (name) => apiFetch(`/api/knowledge/classes/${encodeURIComponent(name)}`),
  knowledgeEvents: (scope) => {
    const p = scope ? `?scope=${encodeURIComponent(scope)}` : "";
    return apiFetch(`/api/knowledge/events${p}`);
  },
  knowledgeEvent: (name) => apiFetch(`/api/knowledge/events/${encodeURIComponent(name)}`),
  knowledgePatterns: (category, search) => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (search) p.set("search", search);
    return apiFetch(`/api/knowledge/patterns?${p}`);
  },
  knowledgePattern: (index) => apiFetch(`/api/knowledge/patterns/${index}`),
  createPattern: (data) => apiFetch("/api/knowledge/patterns/custom", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }),
  updatePattern: (id, data) => apiFetch(`/api/knowledge/patterns/custom/${encodeURIComponent(id)}`, {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }),
  deletePattern: (id) => apiFetch(`/api/knowledge/patterns/custom/${encodeURIComponent(id)}`, {
    method: "DELETE",
  }),

  // Script validation — hosted: the ownership-gated per-connection route (same {valid, errors}
  // result, no tool envelope); local: the direct tool call (callers unwrap either via
  // `res?.result || res`).
  validateScript: (code) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
    return apiFetch("/api/tools/script_validate", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
  },

  // DOCX upload — extract text + tags. Hosted: the per-connection extraction-only route
  // (no disk save — the extracted text inlines into the prompt); local: the desktop route
  // (also saves the original and returns saved_to).
  uploadDocx: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const cid = getActiveConnection();
    const url = (getAuthMode() === "hosted" && cid)
      ? `/api/connections/${encodeURIComponent(cid)}/upload/docx`
      : "/api/upload/docx";
    const res = await rawFetch(url, { method: "POST", body: form, headers: { ...authHeader() } });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Image upload — save and return absolute path for the agent to read
  uploadImage: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await rawFetch("/api/upload/image", { method: "POST", body: form, headers: { ...authHeader() } });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return res.json();
  },

  // Text upload (markdown today, plain text later) — returns the content (+ a saved path
  // in local mode only; the hosted route is extraction-only, same reasoning as uploadDocx).
  uploadText: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const cid = getActiveConnection();
    const url = (getAuthMode() === "hosted" && cid)
      ? `/api/connections/${encodeURIComponent(cid)}/upload/text`
      : "/api/upload/text";
    const res = await rawFetch(url, { method: "POST", body: form, headers: { ...authHeader() } });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return res.json();
  },
  workspaceGet: async (name) => {
    const res = await rawFetch(`/api/tenants/${encodeURIComponent(name)}/workspace`, { headers: { ...authHeader() } });
    if (!res.ok) throw new Error(`workspaceGet ${res.status}`);
    return res.json();
  },
  workspacePut: async (name, payload) => {
    const res = await rawFetch(`/api/tenants/${encodeURIComponent(name)}/workspace`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `workspacePut ${res.status}`);
    }
  },
  workspaceDelete: async (name) => {
    const res = await rawFetch(`/api/tenants/${encodeURIComponent(name)}/workspace`, { method: "DELETE", headers: { ...authHeader() } });
    if (!res.ok) throw new Error(`workspaceDelete ${res.status}`);
  },
  // Rewind / checkpoint API — routed through the per-tenant path so the local
  // journal readers work even when SAP login is down (see the needs_session
  // gate in _routes_tools.call_tool + the login-tolerant tenant bootstrap).
  rewindTimeline: ({ recent = 200, since, until, family } = {}) => apiFetch(_tenantTool("rewind_timeline"), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recent,
      ...(since && { since }),
      ...(until && { until }),
      ...(family && { family }),
    }),
  }).then(_unwrapTool),
  rewindEntry: (entry_id) => apiFetch(_tenantTool("rewind_entry"), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry_id }),
  }).then(_unwrapTool),
  rewindPreview: (sel) => apiFetch(_tenantTool("rewind_preview"), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sel),
  }).then(_unwrapTool),
  rewindExecute: (sel) => apiFetch(_tenantTool("rewind_execute"), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sel),
  }).then(_unwrapTool),
  checkpointCreate: (label) => apiFetch(_tenantTool("checkpoint_create"), {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label }),
  }).then(_unwrapTool),

  // Hosted undo endpoints — preview (read-only plan) and execute (reverse job).
  // In hosted mode (getAuthMode()==="hosted" && cid): routes to the ownership-gated
  // control-plane routes POST /api/connections/{cid}/undo/preview and
  // POST /api/connections/{cid}/undo.  In local mode: delegates to rewindPreview /
  // rewindExecute.  Selector fields group_id / entry_ids / upto_entry_id / recent are
  // passed through as-is; checkpoint is not supported in hosted mode.
  // Optional trailing `cid` targets an explicit connection (e.g. a promotion target
  // that isn't the user's active connection); defaults to the active connection when
  // omitted, so existing callers (timeline.js, promotion.js, agent-activity.js) are
  // unaffected.
  undoPreview: (selector, cid) => {
    cid = cid || getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/undo/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selector || {}),
      });
    }
    return api.rewindPreview(selector);
  },

  undoExecute: (selector, opts = {}, cid) => {
    cid = cid || getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/undo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...(selector || {}), confirmed: true, ...opts }),
      });
    }
    return api.rewindExecute(selector);
  },

  // Return metadata for the current active connection ({id, label, env_role, ...}).
  // Fetches GET /api/connections and filters by the active connection ID; cached
  // per-session by connection ID.  Returns null in local/desktop mode where
  // getActiveConnection() is null, or if the fetch fails.
  connectionMeta: () => _fetchConnMeta(),

  // Ownership-gated SAP health probe for the active connection's sidebar dot.
  // Hosted: GET /api/connections/{cid}/health -> {ok, checked_at, latency_ms?, reason?}.
  // Local/desktop: no probe surface, so resolve {ok:true} -- the shared dot logic treats
  // local as connected (the local tenant card's dot is driven separately, by tenantConnecting).
  connectionHealth: (cid) =>
    getAuthMode() === "hosted"
      ? apiFetch(`/api/connections/${encodeURIComponent(cid)}/health`)
      : Promise.resolve({ ok: true }),

  // Audit CSV download URL — local: /api/agent/audit.csv; Slice 2 (hosted) re-points this to
  // /api/connections/{cid}/audit.csv. Callers that need an authenticated fetch-to-blob download
  // (hosted) should replace the anchor-click trigger with a fetch + blob approach in api.js.
  auditCsvUrl: ({ since, until, family } = {}) => {
    const p = new URLSearchParams();
    if (since) p.set("since", since);
    if (until) p.set("until", until);
    if (family) p.set("family", family);
    const qs = p.toString();
    return `/api/agent/audit.csv${qs ? `?${qs}` : ""}`;
  },

  // Audit entry list — hosted: ownership-gated /api/connections/{cid}/audit (returns {entries:[...]});
  // local: delegates to rewindTimeline (returns {groups:[...]}). timeline.js handles both shapes.
  // conversation_id (hosted-only param) scopes the list to one conversation — the per-turn
  // change chip's query; the local branch scopes by group client-side instead.
  auditEntries: async ({ since, until, family, limit, conversation_id } = {}) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      const p = new URLSearchParams();
      if (since) p.set("since", since);
      if (until) p.set("until", until);
      if (family) p.set("family", family);
      if (conversation_id) p.set("conversation_id", conversation_id);
      if (limit != null) p.set("limit", String(limit));
      const qs = p.toString();
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/audit${qs ? `?${qs}` : ""}`);
    }
    // Local mode: rewindTimeline returns {groups:[...]}; api is resolved at call-time (post-construction).
    return api.rewindTimeline({ since, until, family });
  },

  // Download audit CSV — hosted: auth-gated fetch → Blob → temp anchor (apiFetch can't return a Blob);
  // local: direct URL anchor (unauthenticated /api/agent/audit.csv). Caller just awaits this.
  downloadAuditCsv: async ({ since, until, family } = {}) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid) {
      const p = new URLSearchParams();
      if (since) p.set("since", since);
      if (until) p.set("until", until);
      if (family) p.set("family", family);
      const qs = p.toString();
      const csvUrl = `/api/connections/${encodeURIComponent(cid)}/audit.csv${qs ? `?${qs}` : ""}`;
      const res = await rawFetch(csvUrl, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error(`audit CSV download failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "audit.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return;
    }
    // Local mode: direct URL anchor (unauthenticated endpoint)
    const url = api.auditCsvUrl({ since, until, family });
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },

  // Audit per-entry diff — hosted: GET /api/connections/{cid}/audit/{entry_id} (returns {entry_id,
  // tool, family, op, target, before, after, diff}); local: delegates to rewindEntry so both paths
  // return the same shape. timeline.js _openDiff calls this to open the diff panel in both modes.
  auditEntry: (entry_id) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/audit/${encodeURIComponent(entry_id)}`);
    // Local mode: rewindEntry returns {action, id, tool, family, op, ...before, after, diff}
    return api.rewindEntry(entry_id);
  },

  // Decision-trace viewer — local: /api/agent/traces; hosted: per-connection ownership-gated route.
  traces: (limit = 50) => {
    const cid = getActiveConnection();
    return (getAuthMode() === "hosted" && cid)
      ? apiFetch(`/api/connections/${encodeURIComponent(cid)}/traces?limit=${limit}`)
      : apiFetch(`/api/agent/traces?limit=${limit}`);
  },
  traceDetail: (id) => {
    const cid = getActiveConnection();
    return (getAuthMode() === "hosted" && cid)
      ? apiFetch(`/api/connections/${encodeURIComponent(cid)}/traces/${encodeURIComponent(id)}`)
      : apiFetch(`/api/agent/traces/${encodeURIComponent(id)}`);
  },

  // ── Hosted promotion (release-based, per-connection) ──────────────────────
  // The hosted promotion surface is a snapshot -> plan -> apply flow across the user's own
  // connections (ownership-gated on the control plane). It is DISTINCT from the local/single-
  // operator promotionPlan/promotionApply above (those take source/target tenant NAMES and drive
  // /api/promotion/*); these take a connection id + a portable release_id and drive
  // /api/connections/{cid}/promotion/*. There is no local equivalent, so in local/desktop mode
  // (getAuthMode() !== "hosted") each returns an empty shape and never hits the network.

  // Full connection list (hosted) — the promotion panel reads it to find eligible targets
  // (same family + a higher env rung). Empty in local mode.
  connections: () =>
    getAuthMode() === "hosted" ? apiFetch("/api/connections") : Promise.resolve({ connections: [] }),

  // Delete one of the user's own connections (hosted; ownership-gated server-side). On success
  // the server returns {ok:true, deleted:<id>}; its conversations cascade and its vaulted SAP
  // secret is removed. For a PROD connection the server also enforces a typed-confirm gate, so a
  // non-null `typedConfirm` (the label the user typed) is sent as `typed_confirm` in the DELETE
  // body; non-prod deletes send no body (unchanged). Local/desktop mode has no connections concept,
  // so resolve a no-op shape without hitting the network (mirrors connections()' own local guard).
  // Also invalidates the connection-meta cache so a just-deleted connection can't be served stale by
  // connectionMeta().
  deleteConnection: (cid, typedConfirm = null) => {
    if (getAuthMode() !== "hosted" || !cid) return Promise.resolve({ ok: false, deleted: null });
    _connMetaCache = null;
    _connMetaCacheId = null;
    return apiFetch(`/api/connections/${encodeURIComponent(cid)}`, {
      method: "DELETE",
      headers: typedConfirm ? { "Content-Type": "application/json" } : undefined,
      body: typedConfirm ? JSON.stringify({ typed_confirm: typedConfirm }) : undefined,
    });
  },

  // GET .../promotion/releases — releases stored under the given (default: active) connection's family
  // ({releases:[{release_id, source_tenant, created_at, asset_count}]}).
  promotionReleases: (cid) => {
    cid = cid || getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/promotion/releases`);
    return Promise.resolve({ releases: [] });
  },

  // POST .../promotion/snapshot — cid is the SOURCE; snapshots its assets into a persisted
  // Release. Returns {release_id, asset_count, family}.
  promotionSnapshot: (cid, { names, include_optin } = {}) => {
    if (getAuthMode() !== "hosted" || !cid) return Promise.resolve({});
    const body = {};
    if (Array.isArray(names) && names.length) body.names = names;
    if (Array.isArray(include_optin) && include_optin.length) body.include_optin = include_optin;
    return apiFetch(`/api/connections/${encodeURIComponent(cid)}/promotion/snapshot`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
  },

  // POST .../promotion/plan — cid is the TARGET; dry-run apply of a release against it.
  // Returns {plan:[{type, name, action, detail}], dry_run:true}.
  promotionPlanHosted: (cid, release_id) => {
    if (getAuthMode() !== "hosted" || !cid) return Promise.resolve({ plan: [], dry_run: true });
    return apiFetch(`/api/connections/${encodeURIComponent(cid)}/promotion/plan`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ release_id }),
    });
  },

  // POST .../promotion/apply — cid is the TARGET; gated real apply. opts.confirm carries the typed
  // target label required by the PROD confirmation gate. Returns the run record
  // {status, results:[{type, name, status, action, error}], run_id, ...}.
  promotionApplyHosted: (cid, release_id, opts = {}) => {
    if (getAuthMode() !== "hosted" || !cid) return Promise.resolve({ status: "empty", results: [] });
    return apiFetch(`/api/connections/${encodeURIComponent(cid)}/promotion/apply`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ release_id, ...opts }),
    });
  },

  // Error-event viewer — local: /api/agent/errors; hosted: global admin route (no connection scope).
  // Gate is getAuthMode() === "hosted" ALONE — error routes are global/admin, not connection-scoped.
  errors: (limit = 50) =>
    getAuthMode() === "hosted"
      ? apiFetch(`/api/admin/errors?limit=${limit}`)
      : apiFetch(`/api/agent/errors?limit=${limit}`),

  errorDetail: (id) =>
    getAuthMode() === "hosted"
      ? apiFetch(`/api/admin/errors/${encodeURIComponent(id)}`)
      : apiFetch(`/api/agent/errors/${encodeURIComponent(id)}`),

  // Learning review — candidate lessons harvested from agent runs, admin-curated before
  // promotion into the reference knowledge base. HOSTED-ONLY admin route (no local
  // equivalent) — global/admin, not connection-scoped, same as errors()/settings() above.
  // Gate is getAuthMode() !== "hosted" ALONE.
  learningList: (status = "candidate", limit = 50) => {
    if (getAuthMode() !== "hosted") throw new Error("Learning review is available in hosted mode only");
    return apiFetch(`/api/admin/learning?status=${encodeURIComponent(status)}&limit=${limit}`);
  },

  learningDetail: (id) => {
    if (getAuthMode() !== "hosted") throw new Error("Learning review is available in hosted mode only");
    return apiFetch(`/api/admin/learning/${encodeURIComponent(id)}`);
  },

  learningAction: (id, action, body = {}) => {
    if (getAuthMode() !== "hosted") throw new Error("Learning review is available in hosted mode only");
    return apiFetch(`/api/admin/learning/${encodeURIComponent(id)}/${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // Runtime settings (admin/config) — local: /api/agent/settings; hosted: global admin route.
  // Gate is getAuthMode() === "hosted" ALONE — settings are global/admin, not connection-scoped.
  settings: () =>
    getAuthMode() === "hosted"
      ? apiFetch(`/api/admin/settings`)
      : apiFetch(`/api/agent/settings`),

  settingsAudit: (limit = 50) =>
    getAuthMode() === "hosted"
      ? apiFetch(`/api/admin/settings/audit?limit=${limit}`)
      : apiFetch(`/api/agent/settings/audit?limit=${limit}`),

  settingsRevert: (entryId, typedConfirm) => {
    const base = getAuthMode() === "hosted" ? "/api/admin/settings" : "/api/agent/settings";
    const body = (typedConfirm !== undefined && typedConfirm !== null) ? { typed_confirm: typedConfirm } : {};
    return apiFetch(`${base}/audit/${encodeURIComponent(entryId)}/revert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  settingsUpdate: (key, value, typedConfirm) => {
    const base = getAuthMode() === "hosted" ? "/api/admin/settings" : "/api/agent/settings";
    const payload = (typedConfirm !== undefined && typedConfirm !== null)
      ? { value, typed_confirm: typedConfirm }
      : { value };
    return apiFetch(`${base}/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },

  // Push preview + push — re-pointed to per-connection routes in hosted mode.
  // Local/desktop mode keeps the /api/agent/push* calls unchanged.
  pushPreview: (convId, artifact) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/push/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, artifact }),
      });
    return apiFetch("/api/agent/push/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversation_id: convId, artifact }),
    });
  },
  agentPush: (convId, artifact) => {
    const cid = getActiveConnection();
    if (getAuthMode() === "hosted" && cid)
      return apiFetch(`/api/connections/${encodeURIComponent(cid)}/push`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, artifact }),
      });
    return apiFetch("/api/agent/push", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversation_id: convId, artifact }),
    });
  },

  // M12 Wave 1 T8 -- billing/usage (hosted-only, user-global, NOT
  // connection-scoped -- same "hosted alone" gate as errors()/settings()
  // above; callers gate on getAuthMode()==="hosted" before ever calling
  // these, so no internal mode check is needed here, mirroring
  // hostedModels()'s own unconditional-fetch shape). GET /api/usage ->
  // {plan, used_usd, allowance_usd, remaining_usd, under_free_tier,
  // total_usd}; GET /api/usage/by-connection -> {connections:[{connection_id,
  // label, env_role, billed_usd, cost_usd, run_count, success_rate,
  // avg_duration_ms}]} (server/_routes_billing.py).
  usage: () => apiFetch("/api/usage"),
  usageByConnection: () => apiFetch("/api/usage/by-connection"),

};
